import React, { useState, useCallback, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import {
  Banner,
  Button,
  Checkbox,
  FieldBox,
  Spinner,
  NativeInput,
} from '@pushwoosh/dumb-components';
import { Color } from '@pushwoosh/kit-constants';
import { Horizontal, Vertical } from '@pushwoosh/kit-helpers';
import { Text } from '@pushwoosh/kit-typography';

import { useAPI, type PostApiReponsePayload } from '~/src/hooks/useAPI';
import { browserTimezone } from '~/src/helpers/common.helpers';
import { useEnvironment } from '~/src/contexts/environment';
import { trackEvent } from '~/src/helpers/trackEvent';
import { maskEmail } from '~/src/helpers/mask';
import { H1, H3, Paragraph } from '~/src/ui-kit';
import { ButtonLoader } from '~/src/components/ButtonLoader';
import { ContentBox } from '~/src/components/ContentBox';
import { GoogleLogo } from '~/src/components/GoogleLogo';
import { Separator } from '~/src/components/Separator';
import { AgreementText } from '~/src/components/AgreementText';
import { Lang, useLang } from '~/src/polyglot';

import type { InviteSignUpProps, FormValues } from './InviteSignUp.types';
import {
  getPasswordErrorMessage,
  getConfirmPasswordErrorMessage,
} from './InviteSignUp.helpers';

export function InviteSignUp(props: InviteSignUpProps): ReactElement {
  const {
    email,
    inviteCode,
    isLoading = false,
    globalError = '',
  } = props;

  const { callAPI } = useAPI();
  const { productName, getApiOrigin } = useEnvironment();
  const lang = useLang();
  const isAllowGoogleSignUp = productName === 'Pushwoosh';

  const [errorMessage, setErrorMessage] = useState(globalError);

  const {
    watch,
    register,
    handleSubmit,
    getValues,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      password: '',
      confirmPassword: '',
      allowMarketing: false,
      agreement: false,
    },
  });

  const validatePasswords = useCallback((): boolean => {
    const { password, confirmPassword } = getValues();

    return !!password && password === confirmPassword;
  }, [getValues]);

  const onSubmit = useCallback(async (values: FormValues) => {
    const { allowMarketing, password, confirmPassword } = values;
    if (getValues('agreement') !== true) {
      setError('agreement', {
        type: 'required',
        message: lang({ message: 'INVITE.ERROR.MUST_ACCEPT_TERMS' }),
      });
    } else {
      try {
        await callAPI('api/accept-invite-register', {
          code: inviteCode,
          username: email,
          subscribe: allowMarketing ? '1' : '0',
          password,
          confirmPassword,
        }, 'POST')
          .then((response) => {
            const { redirect, message } = response as PostApiReponsePayload;

            if (redirect) {
              trackEvent({
                name: 'REGISTER',
                properties: {
                  email,
                },
              });

              redirect();
            } else {
              setErrorMessage(message);
            }
          });
      } catch (error: any) {
        setErrorMessage(error.message);
      }
    }
  }, [
    callAPI,
    email,
    inviteCode,
    getValues,
    setError,
    lang,
  ]);

  return (
    <ContentBox>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Vertical $gap={24}>
          <Vertical $gap={4}>
            <H1><Lang message="INVITE.TITLE" /></H1>
            <Paragraph>
              <Lang message="INVITE.SUBTITLE" email={maskEmail(email)} />
            </Paragraph>
          </Vertical>
          <Vertical $gap={16}>
            <Vertical $gap={4}>
              <H3><Lang message="INVITE.CREATE_PASSWORD.TITLE" /></H3>
              <Paragraph>
                <Lang message="INVITE.CREATE_PASSWORD.HINT" />
              </Paragraph>
            </Vertical>
            <FieldBox
              title={lang({ message: 'INVITE.FIELD.PASSWORD' })}
              error={!!errors.password && (
                <Text $variant="footnote" $color={Color.DANGER}>
                  {getPasswordErrorMessage(errors.password, lang)}
                </Text>
              )}
            >
              <NativeInput
                autoFocus
                type="password"
                $isErrored={!!errors.password}
                {...register('password', {
                  required: true,
                  minLength: 8,
                })}
              />
            </FieldBox>
            <FieldBox
              title={lang({ message: 'INVITE.FIELD.REPEAT_PASSWORD' })}
              error={errors.confirmPassword && (
                <Text $variant="footnote" $color={Color.DANGER}>
                  {getConfirmPasswordErrorMessage(errors.confirmPassword, lang)}
                </Text>
              )}
            >
              <NativeInput
                autoFocus
                type="password"
                $isErrored={!!errors.confirmPassword}
                {...register('confirmPassword', {
                  required: true,
                  minLength: 8,
                  validate: validatePasswords,
                })}
              />
            </FieldBox>
          </Vertical>
          <Checkbox
            name="allowMarketing"
            label={lang({ message: 'INVITE.CHECKBOX.MARKETING' })}
            isChecked={watch('allowMarketing')}
            onChange={(): void => setValue('allowMarketing', !watch('allowMarketing'))}
          />
          <FieldBox
            error={!!errors.agreement && (
              <Text $variant="footnote" $color={Color.DANGER}>
                {errors.agreement.message}
              </Text>
            )}
          >
            <Checkbox
              name="agreement"
              label={(
                <AgreementText>
                  <Lang message="INVITE.AGREEMENT" />
                </AgreementText>
              )}
              isChecked={watch('agreement')}
              onChange={(): void => setValue('agreement', !watch('agreement'))}
            />
          </FieldBox>
          {(errorMessage && !isSubmitting) && <Banner type="error" title={errorMessage} />}
          <Vertical $gap={12}>
            {isLoading
              ? (
                <ButtonLoader>
                  <Spinner size="small" />
                  <Lang message="INVITE.LOADING" />
                </ButtonLoader>
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    isLoading={isSubmitting}
                  >
                    <Lang message="INVITE.BUTTON.SIGN_UP" />
                  </Button>
                  {isAllowGoogleSignUp && (
                    <>
                      <Separator />
                      {isSubmitting ? (
                        <ButtonLoader>
                          <Horizontal $alignItems="center" $gap={6}>
                            <GoogleLogo opacity="50%" />
                            <Lang message="INVITE.BUTTON.GOOGLE" />
                          </Horizontal>
                        </ButtonLoader>
                      ) : (
                        <Button
                          as="a"
                          type="button"
                          color="secondary"
                          href={`${getApiOrigin('user-api')}/register-with-google?invite=${inviteCode}&timezone=${encodeURIComponent(browserTimezone())}`}
                        >
                          <Horizontal $alignItems="center" $gap={6}>
                            <GoogleLogo />
                            <Lang message="INVITE.BUTTON.GOOGLE" />
                          </Horizontal>
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}
          </Vertical>
        </Vertical>
      </form>
    </ContentBox>
  );
}
